import { EMPTY_VALUE, EMPTY_VALUES, FieldValue } from "../../../Schema";

/**
 * Reading values back out of a record that has been written to for years.
 *
 * A stored attribute does not always hold what its type suggests. Where that is a property
 * of the field rather than of one caller, the field says so here — the same shape as
 * {@link toGxpOption} on `gxpRelevant`, and as the numeric transforms under
 * `equipment/fields/numeric`.
 */

/** The text a record holds where it means "no value". */
const ABSENT_TEXT = "null";

/**
 * Reads stored text, treating the literal string `"null"` as no value.
 *
 * Some writer, long ago, interpolated a missing value into a string. It is not rare: 8321 of
 * 9525 dev records carry it in at least one attribute, `softwareVersion` and `equipmentId`
 * most often. Reading it as text would show a user the word "null" in a form field and let a
 * filter match on it.
 *
 * Declared for **both** inbound directions by every field that uses it. The rule belongs to
 * the attribute, not to the route it arrived by — the same record reaches this code from
 * AppSync in one Lambda and from a DynamoDB read in another, and a coercion that applied to
 * only one of those would make the two disagree about the same stored value. That is not
 * hypothetical: a blanket version of this rule on one side of the proposed-change diff, with
 * the per-field version on the other, reported `manufacturer` as deleted on edits that never
 * touched it.
 *
 * @param value - The stored value.
 * @returns The value, or `EMPTY_VALUE` when it is absent or the placeholder.
 */
export const readStoredText = (value: FieldValue): FieldValue =>
  value === ABSENT_TEXT || EMPTY_VALUES.includes(value as (typeof EMPTY_VALUES)[number]) ?
    EMPTY_VALUE :
    value;

/**
 * Writes an absent value back as the placeholder, for a column an index is built on.
 *
 * The mirror of {@link readStoredText}, and not a courtesy to the old data: DynamoDB drops an
 * item from a secondary index when a key attribute is null or missing, so an attribute that
 * one of the entry table's indexes is keyed on has to hold something. `"null"` is what the
 * AppSync resolvers put there — `Mutation.updateDigitalLabInstrumentRepositoryEntry.req.vtl`
 * does it for five fields, and its own comment says why: "Setting the equipmentId as null due
 * to equipmentId column using in downstream application GSI".
 *
 * A writer that bypasses those resolvers has to keep the same contract. `bulkEditEquipment`
 * writes to the table directly, so without this, clearing one of these fields would store a
 * real null and quietly drop the equipment out of the index that finds it. The cost of
 * getting this wrong is already visible in the data: 662 dev records have no
 * `manufacturer#equipmentModel#serialNumber` attribute at all and so are absent from
 * `IREntryByManufacturerModelAndSNo`, while all 1658 records holding `serialNumber: "null"`
 * are still present in `IREntryByModelNameAndSerialNumber`.
 *
 * @param value - The field value.
 * @returns The value, or the placeholder when there is none.
 */
export const writeStoredText = (value: FieldValue): FieldValue =>
  EMPTY_VALUES.includes(value as (typeof EMPTY_VALUES)[number]) ? ABSENT_TEXT : value;
